import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = { class: "container" }
const _hoisted_3 = {
  key: 0,
  class: "player__content"
}
const _hoisted_4 = { class: "player__control" }
const _hoisted_5 = { class: "player-play" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "player__control-m" }
const _hoisted_11 = {
  key: 1,
  class: "player--loading"
}

import { computed, onMounted, onUnmounted, Raw, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import * as TypedAssert from 'typed-assert'
import CircleSpinner from '@/components/ui/CircleSpinner.vue'

import { Mixpanel } from '@/analytics/mixpanel'
import {
  AudioDoc,
  PlaybackControl,
  PlaybackState,
  ChapterProgressControl,
  ChapterPrevNextControl,
} from '@/services/player'
import { assertNotNull } from '@/helpers/typing'

import PlayerChapterProgress from './ChapterProgress.vue'
import PlayerVolume from './Volume.vue'
import PlayerRate from './Rate.vue'
import { MediaSessionControl } from '@/services/player/control.media-session'
import {
  loadAudioFromNativeStorage,
  isAudioDownloaded,
  androidLocalAudioFileSrc,
} from '@/services/native.storage'
import { platform } from '@/services/platform'
import { Book } from '@/models/interfaces'
import { DocView } from '@/models/doc'


export default /*@__PURE__*/_defineComponent({
  __name: 'Player',
  props: {
    doc: {}
  },
  setup(__props: any) {

const props = __props

const router = useRouter()

// Audio element reference.
const htmlAudio = ref<HTMLAudioElement | null>(null)
const audio = ref<Raw<AudioDoc> | null>(null)

// We initialize this as an empty string to  wait for a potential load of the audio
// file from native storage to finish to prevent the browser from fetching the file
// online in case we have it available offline.
const audioSource = ref('')

// Set to true when the initialization process is over.
const ready = ref(false)

const isPlaybackFromDownload = ref(false)

const playbackState = ref<PlaybackState>('pause')

const playbackControl = ref<Raw<PlaybackControl> | null>(null)
const chapterControl = ref<Raw<ChapterPrevNextControl> | null>(null)
const progressControl = ref<Raw<ChapterProgressControl> | null>(null)
const mediaSessionControl = ref<Raw<MediaSessionControl> | null>(null)

const playbackStartTime = ref(0)

onMounted(async () => {
  const response = await getAudioSource(props.doc)
  audioSource.value = response.audioSource
  isPlaybackFromDownload.value = response.isPlaybackFromDownload

  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/audio
  const element = document.querySelector('audio')
  TypedAssert.isNotNull(element)
  htmlAudio.value = element
  audio.value = new AudioDoc(htmlAudio.value, props.doc)

  // Preload audio when we show the Player component.
  // It is not rendered until we click the button in the sidebar to show it,
  // see the `doc.controls.isPlayerRendered` property and `AudioPlayer` tag
  // in the `@/app/components/doc/reading/Content.vue`.
  audio.value.load()

  playbackControl.value = new PlaybackControl(
    audio.value as AudioDoc,
    playbackUpdate,
  )
  playbackControl.value.initEventListeners()
  chapterControl.value = new ChapterPrevNextControl(
    audio.value as AudioDoc,
    (pageIdx: number) => {
      router.push(props.doc.pageLink(pageIdx).params)
    },
  )
  progressControl.value = new ChapterProgressControl(
    audio.value as AudioDoc,
    props.doc,
  )
  mediaSessionControl.value = await MediaSessionControl.initialize(
    audio.value as AudioDoc,
    playbackControl.value as PlaybackControl,
    chapterControl.value as ChapterPrevNextControl,
  )

  // Hook chapter control to doc.pageNum changes, so when we navigate
  // the text chapters, the playback follows.
  watch(
    () => props.doc.pageNum,
    (newPageNum: number) => {
      assertNotNull(chapterControl.value).go(newPageNum)
    },
  )

  ready.value = true
})

onUnmounted(async () => {
  await mediaSessionControl.value?.releaseMediaSession()
})

/**
 * Playback state update handler.
 */
async function playbackUpdate(state: PlaybackState): Promise<void> {
  if (state === 'play') {
    playbackStartTime.value = Date.now()
  }

  // We transfer from playing to pause / end.
  if (playbackState.value === 'play' && state !== 'play') {
    // Send the mixpanel 'Audio Listen' event.
    const timeDiffInSeconds = Math.floor(
      (Date.now() - playbackStartTime.value) / 1000,
    )
    Mixpanel.trackAudioListen(
      props.doc,
      timeDiffInSeconds,
      isPlaybackFromDownload.value,
    )
  }

  playbackState.value = state
  props.doc.isAudioPlaying = state === 'play'

  await mediaSessionControl.value?.setPlaybackState(
    state === 'play' ? 'playing' : 'paused',
  )
}

const isBook = computed(() => {
  return props.doc.doc_type === 'book'
})

/**
 * Get the url to use as the audio `src` attribute. Will use the downloaded audio data
 * if the book was downloaded to native device storage, or the regular web URL otherwise.
 *
 * On iOS, can have the side effect of transferring the audio data from native storage
 * to the browser cache to make the data accessible. This takes a few seconds.
 */
async function getAudioSource(book: Book): Promise<{
  /**
   * The URL to use as the `src` attribute of the HTML audio element.
   */
  audioSource: string
  /**
   * If the audio playback will be using data from a native storage download.
   */
  isPlaybackFromDownload: boolean
}> {
  if (await platform.isNativeIOSApp()) {
    // In the iOS app, transfer the audio data from native storage into the browser
    // cache, if the data is downloaded to native storage and not yet in browser
    // cache.
    // `isPlaybackFromDownload` will be true if the audio data got transferred
    // from native storage to browser cache now or is still present from a previous
    // transfer.
    // `isPlaybackFromDownload` will be false if the audio gets streamed from
    // the internet, or from browser cache but the cache was not populated through
    // native storage.
    return {
      isPlaybackFromDownload: await loadAudioFromNativeStorage(book),
      audioSource: book.audio?.url || '',
    }
  } else if (
    (await platform.isNativeAndroidApp()) &&
    (await isAudioDownloaded(book))
  ) {
    // If in the Android app and the audio data is downloaded to native storage,
    // use the native storage file directly as the source for the audio element.
    // This is only possible on Android. On iOS, it does not work because of a CORS
    // issue, see https://github.com/shortformhq/main/issues/6169#issuecomment-1225582895
    return {
      isPlaybackFromDownload: true,
      audioSource: await androidLocalAudioFileSrc(book),
    }
  } else {
    // Otherwise, i.e. on Android without the audio data downloaded or on web (where
    // downloads are not supported), simply use the regular web URL of the audio file.
    return {
      isPlaybackFromDownload: false,
      audioSource: book.audio?.url || '',
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("audio", {
      src: audioSource.value,
      style: {"width":"100%"},
      type: "audio/mpeg",
      crossorigin: "anonymous"
    }, null, 8, _hoisted_1),
    _createElementVNode("div", {
      class: _normalizeClass(["player", __props.doc.controls.playerClass])
    }, [
      _createElementVNode("div", _hoisted_2, [
        (
            ready.value &&
            audio.value &&
            playbackControl.value &&
            chapterControl.value &&
            progressControl.value
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(PlayerVolume, {
                  audio: audio.value,
                  class: "player-volume--pc"
                }, null, 8, ["audio"]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("button", {
                    class: "player-play__link player-play__link--backward",
                    disabled: !playbackControl.value.audioLoaded,
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (playbackControl.value.backward()))
                  }, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("span", { class: "iconfont iplayer-reduce" }, null, -1)
                  ]))], 8, _hoisted_6),
                  (isBook.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "player-play__link player-play__link--prev",
                        disabled: !chapterControl.value.hasPrev(),
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (chapterControl.value.prev()))
                      }, [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("span", { class: "iconfont iplayer-prev" }, null, -1)
                      ]))], 8, _hoisted_7))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    class: "player-play__link player-play__link--play",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (playbackControl.value.togglePlayback()))
                  }, [
                    (
                    !playbackControl.value.paused &&
                    playbackControl.value.interruptedByNetwork
                  )
                      ? (_openBlock(), _createBlock(CircleSpinner, {
                          key: 0,
                          "animation-duration": 2000,
                          size: 58,
                          color: '#ffc906'
                        }))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 1,
                          class: _normalizeClass(["iconfont", {
                    'iplayer-pause': !playbackControl.value.paused,
                    'iplayer-play': playbackControl.value.paused,
                  }])
                        }, null, 2))
                  ]),
                  (isBook.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        class: "player-play__link player-play__link--next",
                        disabled: !chapterControl.value.hasNext(),
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (chapterControl.value.next()))
                      }, [...(_cache[7] || (_cache[7] = [
                        _createElementVNode("span", { class: "iconfont iplayer-next" }, null, -1)
                      ]))], 8, _hoisted_8))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    class: "player-play__link player-play__link--forward",
                    disabled: !playbackControl.value.audioLoaded,
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (playbackControl.value.forward()))
                  }, [...(_cache[8] || (_cache[8] = [
                    _createElementVNode("span", { class: "iconfont iplayer-add" }, null, -1)
                  ]))], 8, _hoisted_9)
                ]),
                _createVNode(PlayerRate, {
                  audio: audio.value,
                  class: "player-speed--pc"
                }, null, 8, ["audio"])
              ]),
              _createVNode(PlayerChapterProgress, {
                progressControl: progressControl.value,
                chapterControl: chapterControl.value
              }, null, 8, ["progressControl", "chapterControl"]),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(PlayerVolume, { audio: audio.value }, null, 8, ["audio"]),
                _createVNode(PlayerRate, { audio: audio.value }, null, 8, ["audio"])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createVNode(CircleSpinner, {
                "animation-duration": 2000,
                size: 36,
                color: '#ffc906'
              })
            ]))
      ])
    ], 2)
  ]))
}
}

})